import Header from '@components/DesignLibrary/Header';
import EditWorkflowForm from '@components/EditWorkflowForm';
import ProgressIndicator from '@components/ProgressIndicator';
import useNoodleApi from '@hooks/useNoodleApi';
import TeamsContext from '@providers/Teams/TeamsContext';
import { getCreatorTeam } from '@tsClient/creators';
import { FC, useContext, useEffect, useState } from 'react';
import * as ApiModels from '@typings/api-models';
import * as tsClient from '@tsClient';
import Spacer from '@components/Spacer';
import { ToastTypeVariants } from '@context/ToastContext';
import Buttons from '@/components/Buttons';
import { m } from 'framer-motion';
import * as format from '@format';
import Badge from '@/components/DesignLibrary/Badge';

import s from './WorkflowDetails.module.scss';
import BuildingBlocks from './BuildingBlocks';

type Props = {
  workflowSlug: string;
};

const PAGE = 1;
const PAGE_SIZE = 1000;

const getBadgeColor = (state: Awaited<ReturnType<typeof tsClient.workflows.getWorkflowVersions>>['items'][0]['state']): string => {
  switch (state) {
  case 'current':
    return 'var(--color-success)';
  case 'stale':
    return 'var(--color-error)';
  default:
    return 'var(--color-gray-75)';
  }
};

const WorkflowBadge: FC<{ state: Awaited<ReturnType<typeof tsClient.workflows.getWorkflowVersions>>['items'][0]['state'] }> = ({ state }) => {
  const color = getBadgeColor(state);
  return <Badge color={color} label={state.charAt(0).toUpperCase() + state.slice(1)} />;
};

const WorkflowDetails: FC<Props> = ({ workflowSlug }) => {
  const { creatorId } = useContext(TeamsContext);

  const [workflow, setWorkflow] = useState<ApiModels.CommonWorkflowResponse | null>(null);
  const [versions, setVersions] = useState<Awaited<ReturnType<typeof tsClient.workflows.getWorkflowVersions>> | null>(null);
  const [versionHistoryVisible, setVersionHistoryVisible] = useState(false);
  const [selectedVersion, setSelectedVersion] = useState<number | null>(null);

  const { data: workflowData, getData: getWorkflowDraftFn } = useNoodleApi(tsClient.workflows.getWorkflowDraft);
  const { data: workflowVersions, getData: getWorkflowVersionsFn } = useNoodleApi(tsClient.workflows.getWorkflowVersions);
  const { data: workflowVersion, getData: getWorkflowVersionFn } = useNoodleApi(tsClient.workflows.getWorkflowVersion);
  const {
    fetchingState: { isFetching: isPublishingWorkflow },
    getData: publishWorkflowFn,
  } = useNoodleApi(tsClient.workflows.publishWorkflow, {
    toastErrorMessage: () => [ToastTypeVariants.ERROR, 'Failed to publish workflow'],
    toastOnError: true,
    toastSuccessMessage: () => [ToastTypeVariants.SUCCESS, 'Workflow published successfully!'],
  });

  const { data: creatorTeam, getData: getCreatorTeamFn } = useNoodleApi(getCreatorTeam);

  const refetchWorkflow = async (): Promise<void> => {
    if (creatorId) {
      await getWorkflowDraftFn({ creatorId, workflowSlug });
      await getWorkflowVersionsFn({ creatorId, workflowSlug });
    }
  };

  const publishWorkflow = async (): Promise<void> => {
    if (creatorId) {
      await publishWorkflowFn({ creatorId, workflowSlug });
      await getWorkflowDraftFn({ creatorId, workflowSlug });
      await getWorkflowVersionsFn({ creatorId, workflowSlug });
    }
  };

  useEffect(() => {
    const fetchCreatorTeam = async (): Promise<void> => {
      if (creatorId) {
        await getCreatorTeamFn({
          creatorId,
          page: PAGE,
          perPage: PAGE_SIZE,
        });
      }
    };

    if (creatorId) {
      getWorkflowDraftFn({ creatorId, workflowSlug });
      getWorkflowVersionsFn({ creatorId, workflowSlug });
      fetchCreatorTeam();
    }
  }, [creatorId, getCreatorTeamFn, getWorkflowDraftFn, getWorkflowVersionsFn, workflowSlug]);

  useEffect(() => {
    if (workflowVersions) {
      setVersions(workflowVersions);
    }
  }, [workflowVersions]);

  useEffect(() => {
    if (workflowData) {
      setWorkflow(workflowData);
      setSelectedVersion(workflowData.version);
    }
  }, [workflowData]);

  useEffect(() => {
    if (selectedVersion && creatorId) {
      getWorkflowVersionFn({ creatorId, version: selectedVersion, workflowSlug });
    }
  }, [creatorId, getWorkflowVersionFn, workflowSlug, selectedVersion]);

  useEffect(() => {
    if (workflowVersion) {
      setWorkflow(workflowVersion);
    }
  }, [workflowVersion]);

  if (workflow === null || versions === null) {
    return (
      <div className={s.form}>
        <Spacer size={20} />
        <ProgressIndicator isCentered />
      </div>
    );
  }

  const selectedVersionState = versions.items.find(version => version.version === workflow.version)?.state;

  return (
    <>
      <div className={s.wrapper}>
        {versionHistoryVisible && (
          <m.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className={s.versions}>
            <Header title="Version history" hierarchy="h3" />
            <ul>
              {versions.items.map(version => (
                <li key={version.id}>
                  <Buttons
                    isWrapper
                    onClick={() => setSelectedVersion(version.version)}
                    className={selectedVersion === version.version ? s.selectedVersion : undefined}
                  >
                    <p className="body-sm">
                      Version {version.version} <WorkflowBadge state={version.state} />
                    </p>
                    <p className="caption">
                      {version.publishedAt
                        ? (<>Published on {format.datetimeLocalTZ.withoutTimezone({ datetime: version.publishedAt })}</>)
                        : (
                          <span style={{ color: 'var(--color-gray-50)' }}>Not published yet</span>
                        )}
                    </p>
                  </Buttons>
                </li>
              ))}
            </ul>
          </m.div>
        )}
        <div className={s.main}>
          {selectedVersion === workflow.version
            ? <>
              <Header
                title={workflow.name}
                description={
                  <p className={s.editingVersionLabel}>
                    Editing Version {workflow.version} {selectedVersionState && <WorkflowBadge state={selectedVersionState} />} ·{' '}
                    <Buttons isWrapper onClick={() => setVersionHistoryVisible(!versionHistoryVisible)} className={s.versionsButton}>
                      {versionHistoryVisible ? 'Hide' : 'Show'} Version History
                    </Buttons>
                  </p>
                }
                actions={
                  versions.items.find(({ state }) => state === 'draft')?.version === selectedVersion
                    ? [
                      {
                        isFetching: isPublishingWorkflow,
                        isPrimary: true,
                        label: 'Publish',
                        onClick: publishWorkflow,
                      },
                    ]
                    : undefined
                }
              />
              <section className={s.section}>
                <div className={s.form}>
                  <EditWorkflowForm
                    canEdit={selectedVersionState === 'draft'}
                    creatorTeamMembers={creatorTeam?.items || []}
                    workflow={workflow}
                    onSave={refetchWorkflow}
                    refetchWorkflow={refetchWorkflow}
                  />
                </div>
                <div className={s.buildingBlocks}>
                  <BuildingBlocks refetchWorkflow={refetchWorkflow} usedBlocks={workflow.buildingBlocks} />
                </div>
              </section>
            </>
            : <><Spacer size={40} /><ProgressIndicator isCentered /></>}
        </div>
      </div>
    </>
  );
};
export default WorkflowDetails;
